import React, { useState } from 'react';
import TableContainer from 'Components/Common/TableContainerReactTable';
import { Card, CardHeader, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, CardBody, Col, Spinner } from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { builderDocumentsListing, updateBuilderDocumentStatus } from 'endpoints/builder/builderDetails.action';
import { toast } from 'react-toastify';
import { formatPropertyType, getTruncatedId } from 'lib/utils/formatters';
import RejectionReasonModal from './RejectionReasonModal';

const statusOptions = [
    { value: 'accepted', label: 'Accepted' },
    { value: 'pending', label: 'Pending' },
    { value: 'rejected', label: 'Rejected' },
];

const DocumentsTable = () => {
    const customPageSize = 10;
    const builderId = useSelector((state: any) => state.Builder?.selectedBuilderId);
    const [data, setData] = useState<any[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState<{ [key: number]: boolean }>({});
    const [updatingRow, setUpdatingRow] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Rejection modal states
    const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
    // eslint-disable-next-line
    const [_rejectionReason, setRejectionReason] = useState('');
    const [pendingRejectionData, setPendingRejectionData] = useState<{
        rowIndex: number;
        docId: string;
        prevSnapshot: any[];
    } | null>(null);

    const formatFileSize = (size: any): string => {
        // Coerce strings and other types to number safely
        const n = typeof size === 'string' && size.trim() === '' ? NaN : Number(size);
        if (!isFinite(n) || isNaN(n)) return 'N/A';
        if (n < 1024) return `${n} bytes`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB`;
        return `${(n / (1024 * 1024)).toFixed(2)} MB`;
    };

    // Fetch documents for the selected builder. API returns the full list; we'll do search & pagination locally.
    const { data: apiData, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['builderDocuments', builderId],
        queryFn: () => builderDocumentsListing(String(builderId)),
        enabled: !!builderId,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        retry: 3
    });

    const updateStatusMutation = useMutation<any, any, { builder_id: string, document_id: string, status: string }>(
        {
            mutationFn: (payload: { builder_id: string, document_id: string, status: string }) => updateBuilderDocumentStatus(payload),
        }
    );

    // Use only API data, no fallback to dummy data
    const apiRows: any[] = apiData?.data?.data ?? apiData?.data ?? [];
    // const allDocs = apiRows;

    // // Local search by file name
    // const filteredDocs = useMemo(() => {
    //     if (!searchInput) return data; // Use data state instead of allDocs
    //     const q = searchInput.toLowerCase();
    //     return data.filter(d => (d.fileName || d.file_name || '').toString().toLowerCase().includes(q));
    // }, [data, searchInput]); // Use data instead of allDocs

    // Reflect API data into local editable state when it arrives
    React.useEffect(() => {
        setData(apiRows); // Use apiRows directly instead of allDocs
        // eslint-disable-next-line
    }, [apiData]); // Use apiData (the actual query result) to prevent infinite loop

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
        setCurrentPage(1);
    };

    const clearSearch = () => {
        setSearchInput('');
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const totalCount = data.length || 0;
    const totalPages = Math.ceil(totalCount / customPageSize);

    // Handle rejection confirmation from modal
    const handleRejectionConfirm = async (finalReason: string) => {
        if (!pendingRejectionData || !finalReason.trim()) {
            toast.error('Rejection reason is required');
            return;
        }

        const { rowIndex, docId } = pendingRejectionData;

        // Close modal first
        setRejectionModalOpen(false);

        // Process the rejection with reason
        await processStatusUpdate('rejected', rowIndex, docId, finalReason);

        // Clean up after API call completes
        setPendingRejectionData(null);
        setRejectionReason('');
    };

    // Handle modal cancellation
    const handleRejectionCancel = () => {
        // Simply close modal without any status change
        setRejectionModalOpen(false);
        setPendingRejectionData(null);
        setRejectionReason('');
        // No API call, no status change - everything remains as it was
    };

    // Function to process status update (moved outside of column definition)
    const processStatusUpdate = async (status: string, rowIndex: number, docId: string, reason?: string) => {
        // optimistic update
        const prevSnapshot = [...data];
        setData(prevState => prevState.map((doc, idx) => idx === rowIndex ? { ...doc, status } : doc));
        setUpdatingRow(rowIndex);

        try {
            const payload: any = {
                builder_id: String(builderId),
                document_id: docId,
                status
            };

            // Add rejection reason if provided
            if (reason && status === 'rejected') {
                payload.rejection_reason = reason;
            }

            await updateStatusMutation.mutateAsync(payload);
            toast.success('Status updated successfully');
        } catch (err: any) {
            // rollback on error
            setData(prevSnapshot);
            toast.error(err?.message || 'Failed to update status');
        } finally {
            setUpdatingRow(null);
        }
    };

    // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 2 seconds
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'No',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const index = (currentPage - 1) * customPageSize + row.index + 1;
                return <span>{index}</span>;
            },
        },
        {
            header: 'Document ID',
            accessorKey: 'id',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const fullId = getValue();
                if (!fullId) return 'N/A';

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        { header: 'File Name', accessorKey: 'file_name', enableColumnFilter: false },
        {
            header: 'Type', accessorKey: 'file_type', enableColumnFilter: false, cell: ({ getValue }: { getValue: () => any }) => {
                const raw = getValue();
                if (!raw) return 'N/A';
                return formatPropertyType(String(raw));
            }
        },
        {
            header: 'Size', accessorKey: 'size', enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const orig = row.original || {};
                const candidate = orig.size ?? orig.file_size ?? orig.fileSize ?? orig.bytes ?? orig.size_in_bytes;
                return formatFileSize(candidate);
            }
        },
        {
            header: 'Created At', accessorKey: 'createdAt', enableColumnFilter: false
            , cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Updated At', accessorKey: 'updatedAt', enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Status',
            accessorKey: 'status',
            enableColumnFilter: false,
            cell: ({ row, getValue }: { row: any, getValue: any }) => {
                const value = getValue();
                const rowIndex = row.index;

                const handleStatusChange = async (newStatus: string) => {
                    const payloadStatus = newStatus.toString().toLowerCase();
                    const currentStatus = value?.toString().toLowerCase();
                    const docId = row.original?.id || row.original?.document_id || row.original?.documentId;

                    if (!docId || !builderId) {
                        toast.error('Missing document or builder id');
                        return;
                    }

                    // Check if the new status is same as current status - if yes, don't proceed
                    if (payloadStatus === currentStatus) {
                        // Close the dropdown if it's open
                        setDropdownOpen(prev => ({ ...prev, [rowIndex]: false }));
                        return;
                    }

                    // If rejected is selected, open the rejection reason modal
                    if (payloadStatus === 'rejected') {
                        const prevSnapshot = [...data];
                        setPendingRejectionData({
                            rowIndex,
                            docId: String(docId),
                            prevSnapshot
                        });
                        setRejectionModalOpen(true);
                        return;
                    }

                    // Handle other statuses (accepted, pending) normally
                    await processStatusUpdate(payloadStatus, rowIndex, String(docId));
                };

                return (
                    <Dropdown isOpen={!!dropdownOpen[rowIndex]} toggle={() => setDropdownOpen(prev => ({ ...prev, [rowIndex]: !prev[rowIndex] }))} size="sm">
                        <DropdownToggle caret color="light" style={{ minWidth: 100 }}>
                            {updatingRow === rowIndex ? (
                                <span className="d-inline-flex justify-content-center align-items-center">
                                    <Spinner size="sm" color="secondary" className="me-2" />
                                    <span className='mt-1'>Updating...</span>
                                </span>
                            ) : (
                                statusOptions.find(opt => opt.value === value)?.label || (value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : 'Select')
                            )}
                        </DropdownToggle>
                        <DropdownMenu>
                            {statusOptions.map(opt => (
                                <DropdownItem
                                    key={opt.value}
                                    active={value === opt.value}
                                    onClick={() => handleStatusChange(opt.value)}
                                >
                                    {opt.label}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>
                );
            }
        },
        {
            header: 'Action',
            accessorKey: 'file_url',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const orig = row.original || {};
                const fileUrl = orig.file_url || orig.downloadUrl || orig.url || '';
                const handleOpenOrDownload = () => {
                    if (!fileUrl) return;

                    const urlNoQuery = fileUrl.split('?')[0].toLowerCase();
                    const extMatch = urlNoQuery.match(/\.([a-z0-9]+)$/);
                    const ext = extMatch ? extMatch[1] : '';

                    if (ext === 'pdf') {
                        // Open PDF in a new tab with noopener for security
                        const a = document.createElement('a');
                        a.href = fileUrl;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        return;
                    }

                    // For non-PDF files, force download using fetch + blob approach
                    // This ensures the file is downloaded rather than opened in browser
                    const filename = (orig.file_name || orig.fileName || 'download').toString();
                    
                    fetch(fileUrl)
                        .then(response => response.blob())
                        .then(blob => {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        })
                        .catch(error => {
                            console.error('Download failed:', error);
                            // Fallback to simple download if fetch fails
                            const link = document.createElement('a');
                            link.href = fileUrl;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        });
                };
                return (
                    <span
                        onClick={handleOpenOrDownload}
                        style={{ color: '#e5e7eb', fontSize: 28, cursor: 'pointer' }}
                        title="Open / Download"
                    >
                        <i className="ri-download-cloud-line" style={{ color: '#000000' }}></i>
                    </span>
                );
            },
        }
    ];

    return (
        <React.Fragment>
            <Card>
                <CardHeader className="d-flex justify-content-end align-items-center">
                    <Col md={3} className="text-end">
                        <div className="position-relative">
                            <Input
                                type="text"
                                placeholder="Search documents..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                className="rounded-2 py-2"
                                style={{
                                    border: '1px solid #e5e7eb',
                                    paddingRight: searchInput ? '40px' : '12px'
                                }}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                                    onClick={clearSearch}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: '0',
                                        width: '30px',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#6c757d',
                                        zIndex: 5
                                    }}
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            )}
                        </div>
                    </Col>
                </CardHeader>
                <CardBody>
                    {/* Loading State */}
                    {(isLoading || isFetching) && (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Loading...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {isError && !(isLoading || isFetching) && (
                        <div className="text-center py-5">
                            <div className="text-danger mb-3">
                                <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h5 className="text-danger">Failed to load data</h5>
                            <p className="text-muted">Please check your connection and try again.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => refetch()}
                            >
                                <i className="ri-refresh-line me-1"></i>
                                Retry
                            </button>
                        </div>
                    )}

                    {!(isLoading || isFetching) && !isError && (
                        <TableContainer
                            columns={columns}
                            data={data}
                            customPageSize={customPageSize}
                            tableClass="table-centered align-middle table-nowrap mb-0"
                            theadClass="text-muted table-light"
                            isServerSide={true}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />
                    )}
                </CardBody>
            </Card>

            {/* Rejection Reason Modal */}
            <RejectionReasonModal
                isOpen={rejectionModalOpen}
                toggle={handleRejectionCancel}
                setRejectionReason={setRejectionReason}
                onConfirm={handleRejectionConfirm}
            />
        </React.Fragment>
    );
};

export default DocumentsTable;
